import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useMotionValueEvent,
  useScroll,
} from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "The Science", to: "/science" },
  { label: "Trifit.life", to: "/transformation-kit" },
  { label: "Trifit XPS", to: "/trifit-xps" },
  { label: "Trifinity", to: "/trifinity" },
  { label: "About Us", to: "/about" },
];

const Nav = () => {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(y > prev && y > 160);
    setScrolled(y > 40);
  });

  const light = location.pathname === "/trifit-xps" && !scrolled && !open;

  return (
    <>
      <motion.header
        data-testid="main-nav"
        animate={{ y: hidden && !open ? "-100%" : "0%" }}
        transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled || open
            ? light
              ? "border-b border-[#17151A]/10 bg-[#F5F3F0]/85 backdrop-blur-md"
              : "border-b border-line/60 bg-ink/80 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
          <Link
            to="/"
            data-testid="nav-logo"
            className={`font-display text-lg font-medium tracking-[0.22em] ${light ? "text-[#17151A]" : "text-white"}`}
          >
            TRIFIT
            <span className={`ml-2 hidden font-mono text-[10px] font-normal tracking-[0.2em] sm:inline ${light ? "text-[#17151A]/50" : "text-ash"}`}>
              PERSONAL INTELLIGENCE
            </span>
          </Link>

          <nav
            data-testid="nav-links"
            className="hidden items-center gap-9 lg:flex"
          >
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`group relative font-mono text-[11px] tracking-[0.14em] transition-colors duration-300 ${
                  location.pathname === l.to
                    ? light ? "text-[#17151A]" : "text-white"
                    : light ? "text-[#17151A]/55 hover:text-[#17151A]" : "text-ash hover:text-fog"
                }`}
              >
                {l.label.toUpperCase()}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px bg-signal transition-[width] duration-300 ${
                    location.pathname === l.to ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            ))}
            <a
              href={location.pathname === "/trifit-xps" ? "https://trifitxps.com" : "https://xsdfem-1j.myshopify.com/"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta"
              className={`border px-5 py-2 font-mono text-[11px] tracking-[0.14em] transition-colors duration-300 hover:border-signal ${
                light ? "border-[#17151A]/40 bg-[#17151A] text-white" : "border-white/25 text-white hover:text-white"
              }`}
            >
              {location.pathname === "/trifit-xps" ? "GET TRIFIT XPS" : "GET TRIFIT"}
            </a>
          </nav>

          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(!open)}
            className={`lg:hidden ${light ? "text-[#17151A]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8 lg:hidden"
          >
            <div className="space-y-7">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className="font-display text-3xl font-light text-white"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <a
                  href={location.pathname === "/trifit-xps" ? "https://trifitxps.com" : "https://xsdfem-1j.myshopify.com/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-cta"
                  onClick={() => setOpen(false)}
                  className="mt-6 inline-block border border-white/25 px-8 py-3 font-mono text-xs tracking-[0.14em] text-white"
                >
                  {location.pathname === "/trifit-xps" ? "GET TRIFIT XPS" : "GET TRIFIT"}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
